import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { IAIAgentTemplateAttachment } from '../../../../../extViewmodels/sdk/models/AI';
import { usePromptFileAttachmentMutation } from '../../../../../queries/Attachments/usePromptFileAttachmentMutation';
import { Button } from '../../../../../web/components-v2/Button';
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from '../../../../../web/components-v2/Collapsible';
import { AIAgentTemplateAttachmentItem } from './AIAgentTemplateAttachmentItem';
import { styles } from './styles';

interface AIAgentTemplateAttachmentsProps {
	attachments: IAIAgentTemplateAttachment[];
	onChange: (attachments: IAIAgentTemplateAttachment[]) => void;
	templateId?: string;
}

export function AIAgentTemplateAttachments({ attachments, onChange, templateId }: AIAgentTemplateAttachmentsProps) {
	const fileInputRef = React.useRef<HTMLInputElement>(null);
	const [validationError, setValidationError] = React.useState<string | null>(null);
	const [isExpanded, setIsExpanded] = React.useState(false);

	const fileAttachmentMutation = usePromptFileAttachmentMutation({
		onSuccess: (result: Api.IFileAttachment[]) => {
			// Add the new attachments to the existing ones
			const newAttachments: IAIAgentTemplateAttachment[] = result.map(fileAttachment => ({
				fileAttachment,
				vectorStoreFileId: undefined as string | undefined, // Will be set by the backend
			}));
			onChange([...attachments, ...newAttachments]);
			setValidationError(null); // Clear any previous errors
			setIsExpanded(true); // Expand to show the new attachments
		},
		onError: (error: Api.IOperationResultNoValue) => {
			console.error('Error uploading files:', error);
		},
	});

	const handleFileSelect = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		fileInputRef.current?.click();
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(event.target.files || []);
		if (files.length === 0) return;

		// Define allowed extensions
		const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.md'];

		// Validate file extensions
		const validFiles: File[] = [];
		const invalidFiles: string[] = [];

		files.forEach(file => {
			const fileName = file.name.toLowerCase();
			const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

			if (hasValidExtension) {
				validFiles.push(file);
			} else {
				invalidFiles.push(file.name);
			}
		});

		// Show error for invalid files
		if (invalidFiles.length > 0) {
			const errorMessage = `Unsupported file type${invalidFiles.length > 1 ? 's' : ''}: ${invalidFiles.join(', ')}. Only PDF, DOC, DOCX, TXT, and MD files are supported.`;
			setValidationError(errorMessage);
		} else {
			setValidationError(null);
		}

		// Upload valid files if any
		if (validFiles.length > 0) {
			fileAttachmentMutation.mutate({ files: validFiles });
		}

		// Clear the input value to allow re-selecting the same file
		event.target.value = '';
	};

	const handleRemoveAttachment = (index: number) => {
		const newAttachments = attachments.filter((_, i) => i !== index);
		onChange(newAttachments);
	};

	const attachmentCount = attachments.length;

	return (
		<div className={css(styles.container)}>
			<Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
				<CollapsibleTrigger className={css(styles.collapsibleHeader)}>
					<span className={css(styles.collapsibleIcon, isExpanded && styles.collapsibleIconOpen)}>&#9654;</span>
					<span className={css(styles.collapsibleTitle)}>Attachments</span>
					{attachmentCount > 0 && <span className={css(styles.collapsibleCount)}>{attachmentCount}</span>}
				</CollapsibleTrigger>
				<CollapsiblePanel>
					<div className={css(styles.collapsibleContent)}>
						<div className={css(styles.uploadContainer)}>
							<div className={css(styles.uploadText)}>Upload files to include as context for the AI Agent</div>
							<Button variant='secondary' onClick={handleFileSelect} isLoading={fileAttachmentMutation.isPending}>
								Select Files
							</Button>
							<input
								ref={fileInputRef}
								type='file'
								multiple
								className={css(styles.fileInput)}
								onChange={handleFileChange}
								accept='.pdf,.doc,.docx,.txt,.md'
							/>
							{validationError && <div className={css(styles.errorMessage)}>{validationError}</div>}
						</div>

						{attachments.length > 0 ? (
							<div className={css(styles.attachmentsList)}>
								{attachments.map((attachment, index) => (
									<AIAgentTemplateAttachmentItem
										key={attachment.fileAttachment.id}
										attachment={attachment}
										templateId={templateId}
										onRemove={() => handleRemoveAttachment(index)}
									/>
								))}
							</div>
						) : (
							<div className={css(styles.emptyState)}>No attachments added yet</div>
						)}
					</div>
				</CollapsiblePanel>
			</Collapsible>
		</div>
	);
}
